import React from 'react';
import { Ratings } from '../../api';
import { ratingColor, gradeColor } from '../../util/ratings';

const MUTED = '#5f6b7a';

/** A single 1–5 headline rating, colored; shows … while loading, — when absent. */
export const RatingBadge: React.FC<{ value?: number | null; loading?: boolean }> = ({ value, loading }) => {
    if (value == null || Number.isNaN(value)) {
        return <span style={{ color: MUTED }}>{loading ? '…' : '—'}</span>;
    }
    return <span style={{ color: ratingColor(value), fontWeight: 600 }}>{value.toFixed(2)}</span>;
};

const FACTORS: { key: keyof Ratings; short: string; label: string }[] = [
    { key: 'valueGrade', short: 'V', label: 'Value' },
    { key: 'growthGrade', short: 'G', label: 'Growth' },
    { key: 'profitabilityGrade', short: 'P', label: 'Profitability' },
    { key: 'momentumGrade', short: 'M', label: 'Momentum' },
    { key: 'epsRevisionsGrade', short: 'E', label: 'EPS Revisions' },
];

// Analyst (SA Authors) recommendation buckets, best→worst. Strong Sell has no current field on the
// API (only 30/90-day), so its `cur` is omitted and contributes 0 to the "now" distribution.
type AuthorBucket = { cur?: keyof Ratings; d30: keyof Ratings; d90: keyof Ratings; label: string; color: string };
const AUTHOR_BUCKETS: AuthorBucket[] = [
    { cur: 'authorsRatingStrongBuyCount', d30: 'authorsRatingStrongBuyCount30Day', d90: 'authorsRatingStrongBuyCount90Day', label: 'Strong Buy', color: '#0b6b2e' },
    { cur: 'authorsRatingBuyCount',       d30: 'authorsRatingBuyCount30Day',       d90: 'authorsRatingBuyCount90Day',       label: 'Buy',         color: '#037f0c' },
    { cur: 'authorsRatingHoldCount',      d30: 'authorsRatingHoldCount30Day',      d90: 'authorsRatingHoldCount90Day',      label: 'Hold',        color: '#8d6605' },
    { cur: 'authorsRatingSellCount',      d30: 'authorsRatingSellCount30Day',      d90: 'authorsRatingSellCount90Day',      label: 'Sell',        color: '#cc6b00' },
    {                                     d30: 'authorsRatingStrongSellCount30Day', d90: 'authorsRatingStrongSellCount90Day', label: 'Strong Sell', color: '#d91515' },
];

/**
 * Compact segmented bar of the SA analyst recommendation distribution (Strong Buy → Sell), sized by
 * the current counts. The hover tooltip carries the full detail incl. the 30/90-day trend.
 */
export const AnalystDistributionBar: React.FC<{ ratings?: Ratings | null; loading?: boolean }> = ({ ratings, loading }) => {
    if (!ratings) {
        return <span style={{ color: MUTED }}>{loading ? '…' : '—'}</span>;
    }
    const val = (k?: keyof Ratings): number => {
        const v = k ? ratings[k] : undefined;
        return v != null && !Number.isNaN(Number(v)) ? Number(v) : 0;
    };
    const rows = AUTHOR_BUCKETS.map(b => ({ label: b.label, color: b.color, cur: val(b.cur), d30: val(b.d30), d90: val(b.d90) }));
    const sumCur = rows.reduce((s, r) => s + r.cur, 0);
    const total = ratings.authorsCount != null ? Number(ratings.authorsCount) : sumCur;
    if (sumCur <= 0 && total <= 0) {
        return <span style={{ color: MUTED }}>{loading ? '…' : '—'}</span>;
    }
    const denom = sumCur > 0 ? sumCur : Math.max(total, 1);
    const title = [
        `${total} analyst${total === 1 ? '' : 's'}`,
        ...rows
            .filter(r => r.cur > 0 || r.d30 > 0 || r.d90 > 0)
            .map(r => `${r.label}: ${r.cur} (30d ${r.d30} · 90d ${r.d90})`),
    ].join('\n');
    return (
        <span
            data-testid="analyst-distribution"
            title={title}
            style={{ display: 'inline-flex', width: 120, height: 12, borderRadius: 3, overflow: 'hidden', border: '1px solid #e9ebed', verticalAlign: 'middle' }}
        >
            {rows.filter(r => r.cur > 0).map(r => (
                <span key={r.label} aria-label={`${r.label} ${r.cur}`} style={{ width: `${(r.cur / denom) * 100}%`, backgroundColor: r.color }} />
            ))}
        </span>
    );
};

/** Five compact factor-grade chips (V G P M E); shows … while loading, — when absent. */
export const FactorGradeChips: React.FC<{ ratings?: Ratings | null; loading?: boolean }> = ({ ratings, loading }) => {
    if (!ratings) {
        return <span style={{ color: MUTED }}>{loading ? '…' : '—'}</span>;
    }
    return (
        <span style={{ display: 'inline-flex', gap: 8 }}>
            {FACTORS.map(f => {
                const n = ratings[f.key] as number | undefined;
                const hasValue = n != null && !Number.isNaN(n);
                return (
                    <span
                        key={f.short}
                        title={`${f.label}${hasValue ? `: ${n}` : ''}`}
                        style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}
                    >
                        <span style={{ fontSize: 10, color: MUTED }}>{f.short}</span>
                        <span style={{ fontWeight: 600, color: hasValue ? gradeColor(n) : MUTED }}>
                            {hasValue ? n : '—'}
                        </span>
                    </span>
                );
            })}
        </span>
    );
};
